import { useLayoutEffect, useRef } from 'react';

import { type Column, type TableInstance } from 'react-table';

import { DEFAULT_COLUMN } from 'bringg-web/components/bringg-react-table';
import { getTableAndColumnsWidthDiff } from 'bringg-web/components/bringg-react-table/utils';

type Props = {
	tableContainerNode: HTMLElement;
	// Replacing any with Record<string, unknown> doesn't work properly figure out why
	tableInstance: TableInstance<any>;
	columns: Column<any>[];
	tableColumnOrder: string[];
	hiddenTableColumnsSet: Set<string>;
	presetViewsEnabled?: boolean;
};

export const useTableLayoutEffect = ({
	tableContainerNode,
	tableInstance,
	columns,
	tableColumnOrder,
	hiddenTableColumnsSet,
	presetViewsEnabled
}: Props) => {
	const minTableWidthInited = useRef(false);

	useLayoutEffect(() => {
		if (presetViewsEnabled) {
			return;
		}

		if (!minTableWidthInited.current && tableContainerNode && columns) {
			// adjust initial width of the table if there is an empty space present
			// TODO similar logic presents in use-table-columns.tsx needs to be merged to one place and reused
			const currentColumnWidths = tableInstance.state.columnResizing.columnWidths;
			const visibleColumnsWidths = columns.reduce((acc, item) => {
				if (!hiddenTableColumnsSet.has(item.id)) {
					acc.push({
						id: item.id,
						width: item.width || item.maxWidth || currentColumnWidths[item.id] || DEFAULT_COLUMN.width,
						disableResizing: item.disableResizing
					});
				}

				return acc;
			}, []);

			const diff = getTableAndColumnsWidthDiff(visibleColumnsWidths, tableContainerNode.clientWidth);

			if (diff > 0) {
				const adjustableColumns = visibleColumnsWidths.filter(({ disableResizing }) => !disableResizing);
				const additionalWidthPerColumn = Math.floor(diff / adjustableColumns.length);

				const nextColumnWidths = visibleColumnsWidths
					.filter(({ id }) => tableColumnOrder.includes(id))
					.map(item => ({ ...item, width: (item.width as number) + additionalWidthPerColumn }))
					.reduce((acc, { id, width }) => Object.assign(acc, { [id]: width }), {});

				tableInstance.setColumnWidths(nextColumnWidths);
			}

			minTableWidthInited.current = true;
		}
	}, [tableContainerNode, presetViewsEnabled]);
};
